import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useDcStore } from './stores/dcStore'
import AuthScreen from './components/AuthScreen'
import AppLayoutV2 from './components/AppLayoutV2'

// Pages
import Dashboard from './pages/Dashboard'
import TopologyPage from './pages/TopologyPage'
import CalculatorPage from './pages/CalculatorPage'
import EquipmentPage from './pages/EquipmentPage'
import PowerPage from './pages/PowerPage'
import MonitoringPage from './pages/MonitoringPage'
import PlacementPage from './pages/PlacementPage'
import TasksPage from './pages/TasksPage'
import ScenariosPage from './pages/ScenariosPage'
import AdminPage from './pages/AdminPage'
import AdminAuditPage from './pages/AdminAuditPage'
import AdminSettingsPage from './pages/AdminSettingsPage'
import NetBoxIntegration from './components/NetBoxIntegration'
import DataCenterEditorPage from './pages/DataCenterEditorPage'
import DcViewerPage from './pages/DcViewerPage'
import CalculatorRedirectPage from './pages/CalculatorRedirectPage'
import RacksListPage from './pages/RacksListPage'
import { RackWorkspacePage } from './pages/RackWorkspacePage'
import CablesListPage from './pages/CablesListPage'
import ConnectivityPage from './pages/ConnectivityPage'
import DeviceDetailsPage from './pages/DeviceDetailsPage'
import { useAuthStore } from './stores/authStore'
import { api } from './utils/api'
import LoginPage from './pages/LoginPage'
import DiscoveryPage from './pages/DiscoveryPage'

import { ToastContainer } from './components/ui'
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import { ConfirmationDialog } from './components/ConfirmationDialog'
import { calculatorHref } from './utils/floorPlan.logic'
import { rootRedirectDc, resolvePageDcCode } from './utils/dcContext.logic'
import { useFloorAccess } from './hooks/useDataCenterApi'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, userRole } = useAuthStore()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  if (userRole !== 'admin') {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

function ModuleRedirector({ module }: { module: string }) {
  const { activeDcCode, datacenters } = useDcStore()
  const dc = resolvePageDcCode(undefined, activeDcCode, datacenters[0]?.code)
  if (!dc) return <Navigate to="/" replace />
  return <Navigate to={`/${dc}/${module}`} replace />
}

function EquipmentItemRedirector() {
  const { id } = useParams<{ id: string }>()
  const { activeDcCode, datacenters } = useDcStore()
  const dc = resolvePageDcCode(undefined, activeDcCode, datacenters[0]?.code)
  if (!dc || !id) return <Navigate to="/" replace />
  return <Navigate to={`/${dc}/equipment/${id}`} replace />
}

function RootRedirector() {
  const { datacenters, loading, activeDcCode } = useDcStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && datacenters.length > 0) {
      navigate(`/${rootRedirectDc(activeDcCode, datacenters[0].code)}`, { replace: true })
    }
  }, [datacenters, loading, activeDcCode, navigate])

  if (loading || datacenters.length > 0) {
    return <div className="flex items-center justify-center h-full text-text-muted">Загрузка...</div>
  }

  return <Dashboard />
}

function LegacyDatacenterRedirect() {
  const { dcCode, code } = useParams<{ dcCode: string; code: string }>()
  if (!dcCode || !code) return <Navigate to="/" replace />
  return <Navigate to={calculatorHref(dcCode, code)} replace />
}

function LegacyFloorPlanRedirect() {
  const { dcCode } = useParams<{ dcCode: string }>()
  if (!dcCode) return <Navigate to="/" replace />
  return <Navigate to={`/${dcCode}/floor-plan`} replace />
}

function FloorPlanRoute() {
  const { dcCode = '' } = useParams<{ dcCode: string }>()
  const access = useFloorAccess(dcCode)
  if (access.isLoading) return <div role="status">Проверка доступа к плану зала...</div>
  if (access.isError || !access.data?.view) return <div role="alert">403: доступ к плану зала запрещён.</div>
  if (!access.data.edit) return <Navigate to={`/${dcCode}/map`} replace />
  return <DataCenterEditorPage />
}

export default function App() {
  const { setUser, logout, isAuthenticated } = useAuthStore()

  useEffect(() => {
    api.get('/api/auth/me')
      .then(res => {
        if (res.data?.authenticated) {
          setUser(res.data)
        } else if (isAuthenticated) {
          logout()
        }
      })
      .catch(() => {
        if (isAuthenticated) {
          logout()
        }
      })
  }, []) // Bootstrap check runs once on initial mount

  return (
    <WorkspaceProvider>
      <ToastContainer />
      <ConfirmationDialog />
      <Routes>
        <Route path="/auth" element={<AuthScreen />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Top-level redirects to prevent /:dcCode wildcard collision */}
        <Route path="/tasks" element={<ModuleRedirector module="tasks" />} />
        <Route path="/netbox" element={<ModuleRedirector module="netbox" />} />
        <Route path="/monitoring" element={<ModuleRedirector module="monitoring" />} />
        <Route path="/discovery" element={<ModuleRedirector module="discovery" />} />
        <Route path="/racks" element={<ModuleRedirector module="racks" />} />
        <Route path="/equipment" element={<ModuleRedirector module="equipment" />} />
        <Route path="/equipment/:id" element={<EquipmentItemRedirector />} />
        <Route path="/placement" element={<ModuleRedirector module="placement" />} />
        <Route path="/cables" element={<ModuleRedirector module="cables" />} />
        <Route path="/topology" element={<ModuleRedirector module="topology" />} />
        <Route path="/connectivity" element={<ModuleRedirector module="connectivity" />} />
        <Route path="/power" element={<ModuleRedirector module="power" />} />
        <Route path="/scenarios" element={<ModuleRedirector module="scenarios" />} />
        <Route path="/calculator" element={<ModuleRedirector module="calculator" />} />
        <Route path="/floor-plan" element={<ModuleRedirector module="floor-plan" />} />
        <Route path="/edit" element={<ModuleRedirector module="floor-plan" />} />

      {/* Main app with sidebar layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayoutV2 />
          </ProtectedRoute>
        }
      >
        <Route index element={<RootRedirector />} />

        {/* Admin (Global) */}
        <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="admin/audit" element={<AdminRoute><AdminAuditPage /></AdminRoute>} />
        <Route path="admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />

        {/* DC-Specific Module */}
        <Route path=":dcCode">
          <Route index element={<Dashboard />} />
          <Route path="calculator" element={<CalculatorRedirectPage />} />
          <Route path="calculator/:code" element={<CalculatorPage />} />
          <Route path="datacenter/:code" element={<LegacyDatacenterRedirect />} />

          {/* Infrastructure */}
          <Route path="racks" element={<RacksListPage />} />

          <Route path="equipment" element={<EquipmentPage />} />
          <Route path="equipment/:id" element={<DeviceDetailsPage />} />
          <Route path="placement" element={<PlacementPage />} />

          {/* Cabling */}
          <Route path="cables" element={<CablesListPage />} />
          <Route path="topology" element={<TopologyPage />} />
          <Route path="connectivity" element={<ConnectivityPage />} />

          {/* Power */}
          <Route path="power" element={<PowerPage />} />

          {/* Integrations & Tasks */}
          <Route path="netbox" element={<NetBoxIntegration />} />
          <Route path="monitoring" element={<MonitoringPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="scenarios" element={<ScenariosPage />} />
          <Route path="discovery" element={<DiscoveryPage />} />
        </Route>
        
      </Route>

      {/* DC Viewer — full-screen read-only map */}
      <Route
        path="/:code/map"
        element={
          <ProtectedRoute>
            <DcViewerPage />
          </ProtectedRoute>
        }
      />

      {/* Floor Plan Editor — full-screen */}
      <Route
        path="/:dcCode/floor-plan"
        element={
          <ProtectedRoute>
            <FloorPlanRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:dcCode/edit"
        element={
          <ProtectedRoute>
            <LegacyFloorPlanRedirect />
          </ProtectedRoute>
        }
      />


      {/* Rack Workspace — full-screen, no sidebar/header wrapper */}
      <Route
        path="/:dcCode/racks/:rackId"
        element={
          <ProtectedRoute>
            <RackWorkspacePage />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </WorkspaceProvider>
  )
}
